import { useMemo, useState, type FormEvent } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import {
  CalendarDaysIcon,
  CheckCircle2Icon,
  InfoIcon,
  KeyRoundIcon,
  Loader2Icon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  SaveIcon,
  ShieldCheckIcon,
  UserRoundIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import { AvatarUpload } from '@/components/ui/avatar-upload'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getMemberBookings, updateOwnProfile, updateUserPassword } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import type { Booking, Profile } from '@/types/domain'

type ProfileFormState = {
  firstName: string
  lastName: string
  birthDate: string
  street: string
  houseNumber: string
  postalCode: string
  city: string
  phoneNumber: string
  company: string
  avatarUrl: string
}

function optionalValue(value: string): string | null {
  return value.trim() || null
}

function getSessionStart(booking: Booking): Date | null {
  const startsAt = booking.course_sessions?.starts_at
  if (!startsAt) {
    return null
  }

  const date = parseISO(startsAt)
  return Number.isNaN(date.getTime()) ? null : date
}

function BookingsOverviewList({
  bookings,
  isLoading,
  errorMessage,
  emptyMessage,
}: {
  bookings: Booking[]
  isLoading: boolean
  errorMessage?: string
  emptyMessage: string
}) {
  return (
    <>
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : errorMessage ? (
        <p className="text-sm text-destructive">{errorMessage}</p>
      ) : bookings.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ul className="divide-y divide-border/60">
          {bookings.map((booking) => {
            const startsAt = getSessionStart(booking)
            const session = booking.course_sessions

            return (
              <li key={booking.id} className="py-3 first:pt-0 last:pb-0">
                <p className="font-medium">{session?.courses?.name ?? 'Kurs'}</p>
                {startsAt && (
                  <p className="text-sm text-muted-foreground">
                    {format(startsAt, "EEE, dd. MMMM yyyy 'um' HH:mm", { locale: de })}
                  </p>
                )}
                {session?.trainer_name && (
                  <p className="text-xs text-muted-foreground">Trainer: {session.trainer_name}</p>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

export function MemberProfilePage() {
  const profile = useOutletContext<Profile>()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<ProfileFormState>(() => ({
    firstName: profile.first_name ?? '',
    lastName: profile.last_name ?? '',
    birthDate: profile.birth_date ?? '',
    street: profile.street ?? '',
    houseNumber: profile.house_number ?? '',
    postalCode: profile.postal_code ?? '',
    city: profile.city ?? '',
    phoneNumber: profile.phone_number ?? '',
    company: profile.company ?? '',
    avatarUrl: profile.avatar_url ?? '',
  }))
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [comparisonTime] = useState(() => Date.now())

  const bookingsQuery = useQuery({
    queryKey: queryKeys.memberBookings,
    queryFn: () => getMemberBookings({}),
    retry: false,
  })

  const { recentBookings, upcomingBookings } = useMemo(() => {
    const recent: Array<{ booking: Booking; startsAt: Date }> = []
    const upcoming: Array<{ booking: Booking; startsAt: Date }> = []

    for (const booking of bookingsQuery.data ?? []) {
      if (booking.status !== 'confirmed') {
        continue
      }

      const startsAt = getSessionStart(booking)
      if (!startsAt) {
        continue
      }

      if (startsAt.getTime() < comparisonTime) {
        recent.push({ booking, startsAt })
      } else {
        upcoming.push({ booking, startsAt })
      }
    }

    return {
      recentBookings: recent
        .sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime())
        .slice(0, 5)
        .map(({ booking }) => booking),
      upcomingBookings: upcoming
        .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())
        .slice(0, 5)
        .map(({ booking }) => booking),
    }
  }, [bookingsQuery.data, comparisonTime])

  const updateField = (field: keyof ProfileFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const profileMutation = useMutation({
    mutationFn: async () => {
      const firstName = form.firstName.trim()
      const lastName = form.lastName.trim()

      if (!firstName) {
        throw new Error('Bitte einen Vornamen eingeben.')
      }

      if (!lastName) {
        throw new Error('Bitte einen Nachnamen eingeben.')
      }

      await updateOwnProfile({
        first_name: firstName,
        last_name: lastName,
        birth_date: form.birthDate || null,
        street: optionalValue(form.street),
        house_number: optionalValue(form.houseNumber),
        postal_code: optionalValue(form.postalCode),
        city: optionalValue(form.city),
        phone_number: optionalValue(form.phoneNumber),
        company: optionalValue(form.company),
        avatar_url: optionalValue(form.avatarUrl),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      toast.success('Profil erfolgreich aktualisiert.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const passwordMutation = useMutation({
    mutationFn: async () => {
      if (newPassword.length < 6) {
        throw new Error('Das neue Passwort muss mindestens 6 Zeichen lang sein.')
      }

      if (newPassword !== confirmPassword) {
        throw new Error('Die Passwörter stimmen nicht überein.')
      }

      await updateUserPassword(currentPassword, newPassword)
    },
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      toast.success('Passwort erfolgreich geändert.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleProfileSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    profileMutation.mutate()
  }

  const handlePasswordSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    passwordMutation.mutate()
  }

  const displayName = `${form.firstName} ${form.lastName}`.trim() || profile.full_name || 'Mein Profil'

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-8">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
            <UserRoundIcon className="h-6 w-6" />
          </div>
          <h1 className="font-headline text-3xl font-bold tracking-tight">Mein Profil</h1>
        </div>
        <p className="max-w-2xl text-muted-foreground">
          Verwalte deine persönlichen Daten und die Sicherheit deines Kontos.
        </p>
      </div>

      <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/10 shadow-lg">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 left-1/3 h-52 w-52 rounded-full bg-secondary/10 blur-3xl" />
        <CardContent className="relative flex flex-col items-center gap-6 p-6 text-center sm:flex-row sm:p-8 sm:text-left">
          <AvatarUpload
            currentUrl={form.avatarUrl}
            fullName={displayName}
            onUploadComplete={(url) => updateField('avatarUrl', url)}
          />
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-primary">Willkommen zurück</p>
              <h2 className="mt-1 truncate font-headline text-2xl font-bold sm:text-3xl">{displayName}</h2>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <Badge variant="secondary">Teilnehmer</Badge>
              <Badge variant="outline" className="gap-1.5 bg-background/60">
                <CheckCircle2Icon className="h-3.5 w-3.5 text-secondary" />
                Aktives Konto
              </Badge>
            </div>
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground sm:justify-start">
              <MailIcon className="h-4 w-4 shrink-0" />
              <span className="truncate">{profile.email}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      <section aria-label="Meine Kurstermine">
        <Card className="shadow-md">
          <CardHeader className="border-b border-border/60">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarDaysIcon className="h-5 w-5 text-primary" />
              Meine Kurstermine
            </CardTitle>
            <CardDescription>Deine vergangenen und kommenden Kursanmeldungen.</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <Tabs defaultValue="upcoming">
              <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:min-w-80">
                <TabsTrigger value="upcoming">Kommende ({upcomingBookings.length})</TabsTrigger>
                <TabsTrigger value="recent">Vergangene ({recentBookings.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="upcoming" className="mt-4">
                <BookingsOverviewList
                  bookings={upcomingBookings}
                  isLoading={bookingsQuery.isLoading}
                  errorMessage={bookingsQuery.isError ? getErrorMessage(bookingsQuery.error) : undefined}
                  emptyMessage="Keine kommenden Kurse gebucht."
                />
              </TabsContent>
              <TabsContent value="recent" className="mt-4">
                <BookingsOverviewList
                  bookings={recentBookings}
                  isLoading={bookingsQuery.isLoading}
                  errorMessage={bookingsQuery.isError ? getErrorMessage(bookingsQuery.error) : undefined}
                  emptyMessage="Noch keine vergangenen Kurse."
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </section>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <form onSubmit={handleProfileSubmit}>
          <Card className="shadow-md">
            <CardHeader className="border-b border-border/60">
              <CardTitle>Persönliche Angaben</CardTitle>
              <CardDescription>Diese Informationen helfen uns bei deiner Kursbetreuung.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8 pt-6">
              <section className="space-y-4" aria-labelledby="profile-personal-heading">
                <div className="flex items-center gap-2">
                  <UserRoundIcon className="h-4 w-4 text-primary" />
                  <h3 id="profile-personal-heading" className="font-semibold">Grunddaten</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="profile-first-name">Vorname *</Label>
                    <Input
                      id="profile-first-name"
                      autoComplete="given-name"
                      required
                      value={form.firstName}
                      onChange={(event) => updateField('firstName', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-last-name">Nachname *</Label>
                    <Input
                      id="profile-last-name"
                      autoComplete="family-name"
                      required
                      value={form.lastName}
                      onChange={(event) => updateField('lastName', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="profile-email">E-Mail-Adresse</Label>
                    <Input
                      id="profile-email"
                      type="email"
                      value={profile.email}
                      disabled
                      aria-describedby="profile-email-hint"
                      className="bg-muted/60"
                    />
                    <p id="profile-email-hint" className="text-xs text-muted-foreground">
                      Deine Login-E-Mail kann derzeit nicht geändert werden.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-birth-date" className="flex items-center gap-2">
                      <CalendarDaysIcon className="h-4 w-4 text-muted-foreground" />
                      Geburtsdatum
                    </Label>
                    <DatePickerInput
                      id="profile-birth-date"
                      value={form.birthDate}
                      onChange={(value) => updateField('birthDate', value)}
                      placeholder="Geburtsdatum auswählen"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-phone" className="flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                      Telefonnummer
                    </Label>
                    <Input
                      id="profile-phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="z. B. +43 660 1234567"
                      value={form.phoneNumber}
                      onChange={(event) => updateField('phoneNumber', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="profile-company" className="flex items-center gap-2">
                      <InfoIcon className="h-4 w-4 text-muted-foreground" />
                      Anmerkung
                    </Label>
                    <Input
                      id="profile-company"
                      placeholder="Geworben von, Gutscheincode, Firma oder sonstige Info"
                      value={form.company}
                      onChange={(event) => updateField('company', event.target.value)}
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-4 border-t border-border/60 pt-6" aria-labelledby="profile-address-heading">
                <div className="flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4 text-primary" />
                  <h3 id="profile-address-heading" className="font-semibold">Adresse</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-4">
                  <div className="space-y-2 sm:col-span-3">
                    <Label htmlFor="profile-street">Straße</Label>
                    <Input
                      id="profile-street"
                      autoComplete="address-line1"
                      value={form.street}
                      onChange={(event) => updateField('street', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-house-number">Hausnummer</Label>
                    <Input
                      id="profile-house-number"
                      value={form.houseNumber}
                      onChange={(event) => updateField('houseNumber', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-1">
                    <Label htmlFor="profile-postal-code">PLZ</Label>
                    <Input
                      id="profile-postal-code"
                      autoComplete="postal-code"
                      inputMode="numeric"
                      value={form.postalCode}
                      onChange={(event) => updateField('postalCode', event.target.value)}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-3">
                    <Label htmlFor="profile-city">Ort</Label>
                    <Input
                      id="profile-city"
                      autoComplete="address-level2"
                      value={form.city}
                      onChange={(event) => updateField('city', event.target.value)}
                    />
                  </div>
                </div>
              </section>
            </CardContent>
            <CardFooter className="justify-end border-t border-border/60 bg-muted/20 pt-6">
              <Button type="submit" className="min-w-36 gap-2" disabled={profileMutation.isPending}>
                {profileMutation.isPending ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <SaveIcon className="h-4 w-4" />
                )}
                {profileMutation.isPending ? 'Speichert ...' : 'Änderungen speichern'}
              </Button>
            </CardFooter>
          </Card>
        </form>

        <form onSubmit={handlePasswordSubmit}>
          <Card className="border-primary/20 shadow-md lg:sticky lg:top-2">
            <CardHeader>
              <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ShieldCheckIcon className="h-6 w-6" />
              </div>
              <CardTitle>Passwort & Sicherheit</CardTitle>
              <CardDescription>Wähle mindestens 6 Zeichen für dein neues Passwort.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Aktuelles Passwort</Label>
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Neues Passwort</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  required
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Passwort wiederholen</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  required
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
                Verwende ein Passwort, das du nicht für andere Konten nutzt.
              </div>
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                variant="outline"
                className="w-full gap-2"
                disabled={passwordMutation.isPending}
              >
                {passwordMutation.isPending ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRoundIcon className="h-4 w-4" />
                )}
                {passwordMutation.isPending ? 'Passwort wird geändert ...' : 'Passwort ändern'}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </div>
  )
}
